<template>
  <span
    class="play-spinner"
    :class="[`play-spinner--${type}`, `play-spinner--${variant}`]"
    :style="{ '--sp-size': resolvedSize }"
    role="status"
    aria-live="polite"
  >
    <span
      v-if="type === 'dots'"
      class="play-spinner__dots"
      aria-hidden="true"
    ><i /><i /><i /></span>
    <span
      v-else
      class="play-spinner__ring"
      aria-hidden="true"
    />
    <span class="play-spinner__label">{{ label }}</span>
  </span>
</template>

<script>
/**
 * PlaySpinner — inline loading indicator for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces
 * bootstrap-vue's <b-spinner>. Three looks: `border` (ring), `grow` (pulse),
 * and `dots` (three bouncing dots — the on-brand, playful default's sibling).
 * `variant` picks the color role; `current` inherits the surrounding text color
 * (handy inside buttons/links). `size` takes sm/md/lg, a number (px), or a raw
 * CSS length. `label` is read by screen readers (visually hidden).
 *
 * VUE3-MIGRATION: props/slots are already Vue 3-safe — no Vue 2-only constructs.
 */
const NAMED_SIZES = {
  sm: 16,
  md: 22,
  lg: 34,
}

export default {
  name: 'PlaySpinner',
  props: {
    /** border (ring) | grow (pulse) | dots (bouncing) */
    type: {
      type: String,
      default: 'border',
      validator: v => ['border', 'grow', 'dots'].includes(v),
    },
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'secondary', 'highlight', 'accent', 'ink', 'neutral', 'current'].includes(v),
    },
    /** number (px) | 'sm'|'md'|'lg' | raw CSS length */
    size: {
      type: [Number, String],
      default: 'md',
    },
    /** Screen-reader text */
    label: {
      type: String,
      default: 'Loading…',
    },
  },
  computed: {
    resolvedSize() {
      const { size } = this
      if (typeof size === 'number') return `${size}px`
      if (NAMED_SIZES[size]) return `${NAMED_SIZES[size]}px`
      if (/^\d+$/.test(size)) return `${size}px`
      return size
    },
  },
}
</script>

<style lang="scss" scoped>
.play-spinner {
  --sp-color: var(--c-primary);
  display: inline-flex;
  align-items: center;
  vertical-align: middle;

  &--secondary { --sp-color: var(--c-secondary); }
  &--highlight { --sp-color: var(--c-highlight); }
  &--accent    { --sp-color: var(--c-accent); }
  &--ink       { --sp-color: var(--surface-ink); }
  &--neutral   { --sp-color: var(--text-muted); }
  &--current   { --sp-color: currentColor; }

  /* ── Ring (border) ── */
  &__ring {
    width: var(--sp-size, 22px);
    height: var(--sp-size, 22px);
    border: calc(var(--sp-size, 22px) * .16) solid var(--sp-color);
    border-top-color: transparent;
    border-right-color: transparent;
    border-radius: 50%;
    animation: play-spin .7s linear infinite;
  }

  /* ── Grow (pulse) ── */
  &--grow &__ring {
    border: 0;
    background: var(--sp-color);
    animation: play-grow 1s ease-in-out infinite;
  }

  /* ── Dots (bouncing) ── */
  &__dots {
    display: inline-flex;
    gap: calc(var(--sp-size, 22px) * .2);

    i {
      width: calc(var(--sp-size, 22px) * .34);
      height: calc(var(--sp-size, 22px) * .34);
      border-radius: 50%;
      background: var(--sp-color);
      animation: play-spinner-bounce .6s var(--ease-bounce) infinite;
    }

    i:nth-child(2) { animation-delay: .1s; }
    i:nth-child(3) { animation-delay: .2s; }
  }

  /* ── Visually-hidden a11y label ── */
  &__label {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}

@keyframes play-spin {
  to { transform: rotate(360deg); }
}

@keyframes play-grow {
  0%   { transform: scale(0); opacity: 1; }
  100% { transform: scale(1); opacity: 0; }
}

@keyframes play-spinner-bounce {
  0%, 100% { transform: translateY(0); opacity: .5; }
  50%      { transform: translateY(-40%); opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .play-spinner__ring {
    animation: play-spinner-fade 1.1s ease-in-out infinite;
    border-top-color: var(--sp-color);
    border-right-color: var(--sp-color);
  }

  .play-spinner--grow .play-spinner__ring,
  .play-spinner__dots i {
    animation: play-spinner-fade 1.1s ease-in-out infinite;
    transform: none;
  }
}

@keyframes play-spinner-fade {
  0%, 100% { opacity: .35; }
  50%      { opacity: 1; }
}
</style>
